import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Briefcase, Send, Clock, CheckCircle2, XCircle, Pin, FileText, Loader2 } from 'lucide-react';
import { solicitationsAPI, jobsAPI, matchesAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { ScheduleInterviewButton } from '../interviews/ScheduleInterviewDialog';
import { MatchBadge, useMatches } from '../match/MatchBadge';

const JobRow = ({ job, status, fixed, isPartner, busy, onInvite, onFix, candidate, match }) => {
  const fixBtn = <Button size="sm" onClick={onFix} disabled={busy} className="bg-green-600 hover:bg-green-700" data-testid={`fix-job-${job.id}`}><CheckCircle2 className="w-4 h-4 mr-1" />Fixar na vaga</Button>;
  let action;
  if (fixed) action = <span className="text-xs text-green-700 flex items-center gap-1"><Pin className="w-3 h-3" />Fixado na vaga</span>;
  else if (status === 'APPROVED') action = fixBtn;
  else if (status === 'PENDING') action = <><span className="text-xs text-amber-700 flex items-center gap-1" data-testid={`invite-pending-${job.id}`}><Clock className="w-3 h-3" />Convite enviado</span>{fixBtn}</>;
  else action = (
    <>
      <Button size="sm" variant="outline" onClick={onInvite} disabled={busy} data-testid={`invite-job-${job.id}`}>
        {isPartner ? <FileText className="w-4 h-4 mr-1 text-amber-500" /> : <Send className="w-4 h-4 mr-1" />}
        {status === 'REJECTED' ? 'Convidar de novo' : 'Convidar'}
      </Button>
      {fixBtn}
    </>
  );
  return (
    <div className="rounded-lg border p-2.5 space-y-2" data-testid={`job-invite-row-${job.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm flex items-center gap-2 min-w-0 font-medium"><Briefcase className="w-4 h-4 shrink-0 text-slate-500" /><span className="truncate">{job.titulo}</span></span>
        <MatchBadge match={match} testid={`match-badge-${job.id}`} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {status === 'REJECTED' && !fixed && <span className="text-xs text-red-600 flex items-center gap-1"><XCircle className="w-3 h-3" />Recusou o convite</span>}
        {action}
        <ScheduleInterviewButton jobId={job.id} candidateId={candidate.id} candidateName={candidate.nome} />
      </div>
    </div>
  );
};

export const JobInvitePanel = ({ candidate, jobs, onPartnerInvite, refreshKey }) => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => solicitationsAPI.candidateStatus(candidate.id)
    .then((r) => setState(r.data)).catch(() => setState({ jobs: {}, fixados: [] }));
  useEffect(() => { setState(null); load(); }, [candidate.id, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const invite = async (job) => {
    if (candidate.partner_id) { onPartnerInvite(job); return; }
    setBusy(true);
    try {
      await solicitationsAPI.create({ candidate_id: candidate.id, job_id: job.id });
      toast.success('Convite enviado. O candidato foi avisado no app e por e-mail.');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao enviar convite'); } finally { setBusy(false); }
  };

  const fix = async (job) => {
    setBusy(true);
    try {
      await jobsAPI.selectCandidate(job.id, candidate.id);
      toast.success(`Candidato fixado em "${job.titulo}"`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao fixar candidato'); } finally { setBusy(false); }
  };

  const matches = useMatches(() => matchesAPI.company(jobs.map((j) => ({ candidate_id: candidate.id, job_id: j.id }))),
    jobs.length ? `${candidate.id}|${jobs.map((j) => j.id).join(',')}` : null);

  if (!state) return <Loader2 className="w-5 h-5 animate-spin text-cyan-600" />;
  return (
    <div className="space-y-2" data-testid="job-invite-panel">
      {jobs.map((job) => (
        <JobRow key={job.id} job={job} status={state.jobs[job.id]} fixed={state.fixados.includes(job.id)} match={matches[`${candidate.id}|${job.id}`]}
          isPartner={!!candidate.partner_id} busy={busy} candidate={{ ...candidate, nome: state.nome || candidate.nome }} onInvite={() => invite(job)} onFix={() => fix(job)} />
      ))}
    </div>
  );
};
